import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';
// auth
import { AuthGuard } from 'src/auth/guard';
// layouts
import DashboardLayout from 'src/layouts/dashboard';
// components
import { LoadingScreen } from 'src/components/loading-screen';

import SupportPage from 'src/pages/main-dashboard/support/support-page';
import AccountPage from 'src/pages/dashboard/user/account';
import AccessGuard from 'src/auth/guard/access-guard';
import { PLATFORM_ACCESS } from 'src/config-global';

// OVERVIEW
const ContractorPage = lazy(() => import("src/pages/dashboard/contractor-page"))
const CalendarPage = lazy(() => import('src/pages/dashboard/calendar'));

//invoices
const InvoicesPage = lazy(() => import('src/pages/main-dashboard/invoices/invoices-page'));
const DetailsInvoicePage = lazy(() => import('src/pages/main-dashboard/invoices/detail-invoice-page'));
const CreateInvoicePage = lazy(() => import('src/pages/main-dashboard/invoices/create-invoice-page'));
const TransactionsPage = lazy(() => import('src/pages/main-dashboard/transactions/transaction-page'));
const TransactionDetailPage = lazy(() => import('src/pages/main-dashboard/transactions/transaction-detail-page'));

const InventoryPage = lazy(() => import('src/pages/main-dashboard/inventory/inventory-page'));
const SettingsPage = lazy(() => import('src/pages/main-dashboard/settings/settings-page'));
const PaymentPage = lazy(() => import('src/pages/main-dashboard/settings/payment'));
const CategoriesPage = lazy(() => import('src/pages/main-dashboard/categories/categories-page'));
const RecentActivitiesPage = lazy(() => import('src/pages/main-dashboard/recent-activities/recent-activities-page'));

//jobs routes
const JobsPage = lazy(() => import('src/pages/main-dashboard/jobs/jobs-page'));
const JobCreatePage = lazy(() => import('src/pages/main-dashboard/jobs/create-job-page'));
const JobDetailPage = lazy(() => import('src/pages/main-dashboard/jobs/job-detail-page'));
const JobInventoryPage = lazy(() => import('src/pages/main-dashboard/jobs/inventory-job-page'));

//estimator templatesP
const EstimatorTemplatePage = lazy(() => import('src/pages/main-dashboard/estimator-template/list'));
const CreateEstimatorTemplateView = lazy(() => import('src/pages/main-dashboard/estimator-template/add'));
const DefaultGeographicAdjustor = lazy(() => import('src/pages/main-dashboard/estimator-template/default-geographic-page'));

//clients
const Clients = lazy(() => import('src/pages/main-dashboard/clients/clients-page'));
const ClientDetailPage = lazy(() => import('src/pages/main-dashboard/clients/client-detail-page'));
const ClientEmailPage = lazy(() => import('src/pages/main-dashboard/clients/email'));

//technicians
const TechniciansPage = lazy(() => import('src/pages/main-dashboard/technicians/technicians-page'));
const AssignTechnicianPage = lazy(() => import('src/pages/main-dashboard/technicians/assign-technician-page'));
const TechnicianDetailPage = lazy(() => import('src/pages/main-dashboard/technicians/technician-detail-page'));

// const MetricPage = lazy(() => import('src/pages/main-dashboard/metric/metric-page'));
// const JobTypeListPage = lazy(() => import('src/pages/main-dashboard/job-type/list'));

//quote view
const Quoteview = lazy(() => import('src/pages/main-dashboard/quote/quote-page'));

//estimator settings
const GeographicAdjusterPage = lazy(() => import('src/pages/main-dashboard/estimator-settings-contractor/geographic-adjustor-page'));
const PrimaryServicePage = lazy(() => import('src/pages/main-dashboard/estimator-settings-contractor/primary-service-page'));
const AdditionalServicePage = lazy(() => import('src/pages/main-dashboard/estimator-settings-contractor/additional-service-page'));
const EstimatorTermsPage = lazy(() => import('src/pages/main-dashboard/estimator-settings-contractor/terms-and-conditions-page'));
const MetricPageSetting = lazy(() => import('src/pages/main-dashboard/estimator-settings-contractor/metric-page'));
const JobTypeListPageSetting = lazy(() => import('src/pages/main-dashboard/estimator-settings-contractor/job-type-page'));
const CourseHomePage = lazy(() => import('src/pages/main-dashboard/training-academy/courses/home'));
const CourseHomeDetailPage = lazy(() => import('src/pages/main-dashboard/training-academy/courses/home-detail'));

//support center

const LessonDetailPage = lazy(() => import('src/pages/main-dashboard/training-academy/courses/lesson-detail'));
const ResourceHomePage = lazy(() => import('src/pages/main-dashboard/support-center/resources/home'));
const ResourceHomeDetailPage = lazy(() => import('src/pages/main-dashboard/support-center/resources/home-detail'));
const CollectPaymentPage = lazy(() => import('src/pages/main-dashboard/payments/collect-payment-page'));

// const MyBuildPage = lazy(() => import('src/pages/main-dashboard/support-center/my-build/home'));
const BuildList = lazy(() => import('src/pages/main-dashboard/build-mode/contractor-build-list'));
const BuildDetailPage = lazy(() => import('src/pages/main-dashboard/support-center/my-build/builde-detail'));
const ContractorNotificationPage = lazy(() => import('src/pages/main-dashboard/build-mode/shop-admin-notification'));

const InvoiceNotesPage = lazy(() => import('src/pages/main-dashboard/estimator-settings-contractor/invoice-notes-page'));
const EstimateNotesPage = lazy(() => import('src/pages/main-dashboard/estimator-settings-contractor/estimate-notes-page'));

const ReorderPrimaryServicePage = lazy(() => import('src/pages/main-dashboard/estimator-settings-contractor/reorder-primary-service-page'));

const MessageBoard = lazy(() => import('src/pages/main-dashboard/build-mode/messge-board'));

export const dashboardRoutes = [
  {
    path: 'contractor',
    element: (
      <AuthGuard>
        <DashboardLayout>
          <Suspense fallback={<LoadingScreen />}>
            <Outlet />
          </Suspense>
        </DashboardLayout>
      </AuthGuard>
    ),
    children: [
      {
        index: true,
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <ContractorPage />
          </AccessGuard>
        ),
      },
      {
        path: 'jobs',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <JobsPage />, index: true },
          { path: 'create', element: <JobCreatePage /> },
          { path: ':id', element: <JobDetailPage /> },
          { path: ':id/inventory', element: <JobInventoryPage /> },
        ],
      },
      {
        path: 'client',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <Clients />, index: true },
          { path: ':id', element: <ClientDetailPage /> },
          { path: 'email-bulk', element: <ClientEmailPage /> },
        ],
      },
      {
        path: 'inventory',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <InventoryPage />, index: true },
        ],
      },

      { path: 'quote', element: <Quoteview /> },

      {
        path: 'technicians',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <TechniciansPage />, index: true },
          { path: 'assign', element: <AssignTechnicianPage /> },
          { path: ':id', element: <TechnicianDetailPage /> },
        ],
      },
      {
        path: 'estimator-templates',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <EstimatorTemplatePage />, index: true },
          { path: 'create', element: <CreateEstimatorTemplateView /> },
          { path: 'geographic-adjustor', element: <DefaultGeographicAdjustor /> },
        ],
      },

      {
        path: 'recent-activity',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <RecentActivitiesPage />, index: true },
        ],
      },

      { path: 'categories', element: <CategoriesPage /> },

      {
        path: 'invoices',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <InvoicesPage />, index: true },
          { path: 'create', element: <CreateInvoicePage /> },
          { path: ':id', element: <DetailsInvoicePage /> },
        ],
      },

      {
        path: 'transactions',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <TransactionsPage />, index: true },
          { path: ':id', element: <TransactionDetailPage /> },
        ],
      },

      {
        path: 'settings',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <SettingsPage />, index: true },
          { path: 'payment', element: <PaymentPage /> },
          {
            path: 'estimator',
            children: [
              { element: <GeographicAdjusterPage />, index: true },
              { path: 'geographic-adjustor', element: <GeographicAdjusterPage /> },
              { path: 'primary-service', element: <PrimaryServicePage /> },
              { path: 'additional-service', element: <AdditionalServicePage /> },
              { path: 'terms', element: <EstimatorTermsPage /> },
              { path: 'metric', element: <MetricPageSetting /> },
              { path: 'job-type', element: <JobTypeListPageSetting /> },
              { path: 'invoice-notes', element: <InvoiceNotesPage /> },
              { path: 'estimate-notes', element: <EstimateNotesPage /> },
              { path: 'reorder-items/:type', element: <ReorderPrimaryServicePage /> }
            ],
          },
        ],
      },

      {
        path: 'training-academy',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB, PLATFORM_ACCESS.TRAINING_ACADEMY]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <CourseHomePage />, index: true },
          { path: 'course/:id/detail', element: <CourseHomeDetailPage /> },
          { path: 'course/:id/lesson/:lessonId', element: <LessonDetailPage /> },
        ],
      },

      {
        path: 'support-center',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB, PLATFORM_ACCESS.SUPPORT_CENTER, PLATFORM_ACCESS.SUPPORT_CENTER]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          { element: <ResourceHomePage />, index: true },
          { path: "resources", element: <ResourceHomePage /> },
          // { path: "my-build", element: <MyBuildPage /> },
          // { path: "build-list", element: <BuildList /> },
          // { path: "notifications/:id", element: <ContractorNotificationPage /> },
          // { path: "message-board", element: <MessageBoard /> },
          // { path: "build-detail/:id", element: <BuildDetailPage /> },
          { path: 'resource/:id/detail', element: <ResourceHomeDetailPage /> },
        ],
      },

       {
        path: 'build-mode',
        element: (
          <AccessGuard requiredAccess={[PLATFORM_ACCESS.CUSTOMER_HUB]}>
            <Outlet />
          </AccessGuard>
        ),
        children: [
          // { path: "my-build", element: <MyBuildPage /> },
          { path: "build-list", element: <BuildList /> },
          { path: "notifications/:id", element: <ContractorNotificationPage /> },
          { path: "message-board", element: <MessageBoard /> },
          { path: "build-detail/:id", element: <BuildDetailPage /> },
        ],
      },



      // ── No access guard needed — available to all authenticated users ──
      { path: 'account', element: <AccountPage /> },
      { path: 'calendar', element: <CalendarPage /> },
      { path: 'collect-payments', element: <CollectPaymentPage /> },
      { path: 'contact-support', element: <SupportPage /> },
    ],
  },
];